import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    App,
    Button,
    Form,
    Grid,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Tooltip,
} from 'antd'
import type { TableColumnsType } from 'antd'
import {
    CheckCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    LinkOutlined,
    PlusOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
    buildPaginatedListPagination,
    PAGINATED_LIST_TABLE_SIZE,
    renderPaginatedListText,
} from '../../components/common/PaginatedListTable'
import TypeColorSelect from '../../components/common/TypeColorSelect'
import { RESERVED_TYPE_COLOR } from '../../components/common/typeColorSelectConfig'
import { PageLayout } from '../../components/layout'
import { useDedupedMessage } from '../../hooks/useDedupedMessage'
import { usePermissions } from '../../hooks/usePermissions'
import { localeService } from '../../services/localeService'
import { parseModelPackage } from '../../utils/modelPackage'
import {
    activateModelVersion,
    createModelDisplayCategory,
    createModelProjectConfig,
    createModelProjectTypeConfig,
    deleteModelDisplayCategory,
    deleteModelProjectConfig,
    deleteModelProjectTypeConfig,
    deleteModelVersion,
    getModelClassMappings,
    getModelDisplayCategories,
    getManualRecognitionTypeMappings,
    getManualRecognitionTypes,
    getModelManagementProjects,
    getModelRawClassOptions,
    getScopedModelProjectConfigRecords,
    getModelVersions,
    getScopedModelRuntimeConfigs,
    rollbackModelVersion,
    replaceModelDisplayCategoryBindings,
    updateModelDisplayCategory,
    updateModelDisplayCategoryVisibility,
    updateModelRuntimeConfig,
    updateModelVersionRemark,
    uploadModelVersion,
} from '../../services/modelManagementService'
import type {
    ManualRecognitionTypeMappingSummary,
    ManualRecognitionTypeSummary,
    ModelClassMappingSummary,
    ModelDisplayCategorySummary,
    ModelKey,
    ModelManagementProjectSummary,
    ModelRawClassOptionSummary,
    ModelRuntimeConfigSummary,
    ModelVersionSummary,
} from '../../types/modelManagement'
import {
    MODEL_ACTIVATE_ACCESS_PERMISSIONS,
    MODEL_MAPPING_UPDATE_ACCESS_PERMISSIONS,
    MODEL_UPLOAD_ACCESS_PERMISSIONS,
} from '../../utils/permissions'
import { filterSearchOptionByLabel } from '../../utils/searchText'
import {
    ModelBindingModal,
    ModelDetailModal,
    ModelRemarkModal,
    ModelUploadModal,
} from './modelManagement/ModelManagementModals'
import {
    ModelBindingSection,
    ModelConfigurationSection,
    ModelUploadSection,
} from './modelManagement/ModelManagementSections'
import ManualRecognitionTypesSection from './modelManagement/ManualRecognitionTypesSection'
import NonDismissiblePopconfirm from '../../components/common/NonDismissiblePopconfirm'
import ModelRecognitionTypesSection from './modelManagement/ModelRecognitionTypesSection'
import type {
    BindingFormValues,
    DisplayCategoryKey,
    DisplayCategoryRow,
    ModelConfigurationRow,
    ModelConfigurationStatusKey,
    ModelSelectionStatusKey,
    ModelScopeTabKey,
    ProjectConfigRecordRow,
    RawClassBindingOption,
    RemarkFormValues,
    ScopeSelectorOption,
    UploadFormValues,
} from './modelManagement/pageConfig'
import {
    DEFAULT_MAPPING_SET_ID,
    BINDING_TABLE_SCROLL_X,
    MODEL_CONFIGURATION_DETAIL_MODAL_WIDTH,
    MODEL_CONFIGURATION_STATUS_COLORS,
    MODEL_CONFIGURATION_TABLE_SCROLL_X,
    MODEL_USAGE_TABLE_SCROLL_X,
    MODEL_KEY_OPTIONS,
    MODEL_MANAGEMENT_I18N_PREFIX,
    MODEL_SELECTION_STATUS_COLORS,
    RAW_CLASS_LABEL_KEYS,
    encodeBindingValue,
    encodeProjectScopeValue,
    formatBytes,
    getModelManagementErrorDetail,
    getModelStatusKey,
    normalizeRawClass,
    parseScopeValue,
} from './modelManagement/pageConfig'

interface ModelUsageConfigRow {
    usageKey: string
    scopeType: 'platform' | 'project'
    projectId: number | null
    projectCode: string
    projectName: string
    participateInInference: boolean
}

interface ModelVersionDetailLogContext {
    operationModule: string
    changeAction: string
    clearAction: string
}

export type ModelManagementMode = 'upload' | 'configuration' | 'types'

interface ModelManagementProps {
    mode?: ModelManagementMode
}

interface DisplayCategoryFormValues {
    displayNameZhCn?: string
    color?: string
    syncToProjects?: boolean
}

type RecognitionTypeView = 'custom' | 'model' | 'manual'

const MODEL_VERSION_UPLOAD_LOG_MODULE = '模型管理-模型上传'
const MODEL_VERSION_UPLOAD_USAGE_ACTION_PREFIX = '使用情况-'
const MODEL_VERSION_PLATFORM_CONFIG_LOG_MODULE =
    '模型管理-模型配置-平台默认配置'
const MODEL_VERSION_PROJECT_CONFIG_LOG_MODULE = '模型管理-模型配置-项目配置'
const MODEL_VERSION_CHANGE_CURRENT_LOG_ACTION = '更换当前使用模型'
const MODEL_VERSION_SET_CURRENT_LOG_ACTION = '设为当前使用'
const MODEL_VERSION_CLEAR_CURRENT_LOG_ACTION = '取消当前使用'
const MODEL_VERSION_DELETE_LOG_ACTION = '删除'

const getUploadUsageLogAction = (action: string) =>
    `${MODEL_VERSION_UPLOAD_USAGE_ACTION_PREFIX}${action}`

const getRuntimeConfigLogModule = (scopeType: ModelUsageConfigRow['scopeType']) =>
    scopeType === 'project'
        ? MODEL_VERSION_PROJECT_CONFIG_LOG_MODULE
        : MODEL_VERSION_PLATFORM_CONFIG_LOG_MODULE

const getVersionUploadedAt = (version?: ModelVersionSummary | null): string | null =>
    version?.artifact?.created_at ?? version?.created_at ?? null

const getModelVersionDetailLogContext = (
    operationModule: string,
    entryAction: string,
): ModelVersionDetailLogContext => ({
    operationModule,
    changeAction: `${entryAction}-${MODEL_VERSION_SET_CURRENT_LOG_ACTION}`,
    clearAction: `${entryAction}-${MODEL_VERSION_CLEAR_CURRENT_LOG_ACTION}`,
})

const MODEL_VERSION_DELETE_IN_USE_PATTERNS = [
    'active model version cannot be deleted',
    'model version is still configured as current version',
]

const isModelVersionDeleteInUseError = (detail: string): boolean =>
    MODEL_VERSION_DELETE_IN_USE_PATTERNS.some((pattern) =>
        detail.toLowerCase().includes(pattern),
    )

const isOperatorSelectableRawClassOption = (
    option: RawClassBindingOption,
    boundValueSet: Set<string>,
    bindingCategoryKey: string,
    availableCategoryKeys: Set<string>,
): boolean => {
    if (boundValueSet.has(option.value)) {
        return true
    }
    if (
        option.isMapped &&
        option.displayCategoryKey &&
        availableCategoryKeys.has(option.displayCategoryKey) &&
        option.displayCategoryKey !== bindingCategoryKey
    ) {
        return false
    }
    if (option.isMapped || option.isCurrentModel) {
        return true
    }
    return option.sourceVersions.length > 0
}

const ModelManagement: React.FC<ModelManagementProps> = ({
    mode = 'upload',
}) => {
    const { message: rawMessage, modal, notification } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { i18n, t } = useTranslation(['admin', 'navigation', 'common'])
    const { hasAnyPermissions } = usePermissions()
    const screens = Grid.useBreakpoint()
    const [uploadForm] = Form.useForm<UploadFormValues>()
    const [remarkForm] = Form.useForm<RemarkFormValues>()
    const [bindingForm] = Form.useForm<BindingFormValues>()
    const [displayCategoryForm] = Form.useForm<DisplayCategoryFormValues>()

    const canUpload = hasAnyPermissions(MODEL_UPLOAD_ACCESS_PERMISSIONS)
    const canActivate = hasAnyPermissions(MODEL_ACTIVATE_ACCESS_PERMISSIONS)
    const canUpdateMapping = hasAnyPermissions(
        MODEL_MAPPING_UPDATE_ACCESS_PERMISSIONS,
    )

    const [versions, setVersions] = useState<ModelVersionSummary[]>([])
    const [runtimeConfigs, setRuntimeConfigs] = useState<
        ModelRuntimeConfigSummary[]
    >([])
    const [displayCategories, setDisplayCategories] = useState<
        ModelDisplayCategorySummary[]
    >([])
    const [classMappings, setClassMappings] = useState<
        ModelClassMappingSummary[]
    >([])
    const [manualRecognitionTypes, setManualRecognitionTypes] = useState<
        ManualRecognitionTypeSummary[]
    >([])
    const [manualTypeMappings, setManualTypeMappings] = useState<
        ManualRecognitionTypeMappingSummary[]
    >([])
    const [rawClassOptions, setRawClassOptions] = useState<
        RawClassBindingOption[]
    >([])
    const [projects, setProjects] = useState<ModelManagementProjectSummary[]>(
        [],
    )
    const [projectModelConfigRecords, setProjectModelConfigRecords] = useState<
        ProjectConfigRecordRow[]
    >([])
    const [projectTypeConfigRecords, setProjectTypeConfigRecords] = useState<
        ProjectConfigRecordRow[]
    >([])
    const [projectConfigRecordsLoading, setProjectConfigRecordsLoading] =
        useState(false)
    const [scopeTab, setScopeTab] = useState<ModelScopeTabKey>('platform')
    const [recognitionTypeView, setRecognitionTypeView] =
        useState<RecognitionTypeView>('custom')
    const [activeProjectId, setActiveProjectId] = useState<number | null>(null)
    const [projectConfigDetailOpen, setProjectConfigDetailOpen] =
        useState(false)
    const [projectBindingDetailOpen, setProjectBindingDetailOpen] =
        useState(false)
    const [loading, setLoading] = useState(false)
    const [mappingLoading, setMappingLoading] = useState(false)
    const [creatingProjectConfig, setCreatingProjectConfig] = useState(false)
    const [deletingProjectConfig, setDeletingProjectConfig] = useState(false)
    const [uploadModalOpen, setUploadModalOpen] = useState(false)
    const [remarkVersion, setRemarkVersion] =
        useState<ModelVersionSummary | null>(null)
    const [bindingModalOpen, setBindingModalOpen] = useState(false)
    const [displayCategoryModalOpen, setDisplayCategoryModalOpen] =
        useState(false)
    const [detailModalOpen, setDetailModalOpen] = useState(false)
    const [projectConfigModalOpen, setProjectConfigModalOpen] = useState(false)
    const [projectConfigModalValue, setProjectConfigModalValue] =
        useState<string>()
    const [projectSearch, setProjectSearch] = useState('')
    const [appliedProjectSearch, setAppliedProjectSearch] = useState('')
    const [typeProjectSearch, setTypeProjectSearch] = useState('')
    const [appliedTypeProjectSearch, setAppliedTypeProjectSearch] = useState('')
    const [selectedProjectIds, setSelectedProjectIds] = useState<React.Key[]>(
        [],
    )
    const [batchLoading, setBatchLoading] = useState(false)
    const [batchChangeOpen, setBatchChangeOpen] = useState(false)
    const [batchOperation, setBatchOperation] = useState<
        'change' | 'pause' | 'resume'
    >('change')
    const [batchModelKey, setBatchModelKey] = useState<ModelKey>('mosquito')
    const [batchTargetVersionId, setBatchTargetVersionId] = useState<number>()
    const [usageVersion, setUsageVersion] =
        useState<ModelVersionSummary | null>(null)
    const [usageRows, setUsageRows] = useState<ModelUsageConfigRow[]>([])
    const [selectedUsageConfigKeys, setSelectedUsageConfigKeys] = useState<
        React.Key[]
    >([])
    const [usagePlatformInUse, setUsagePlatformInUse] = useState(false)
    const [usageLoading, setUsageLoading] = useState(false)
    const [usageSearch, setUsageSearch] = useState('')
    const [detailLogContext, setDetailLogContext] =
        useState<ModelVersionDetailLogContext | null>(null)
    const [bindingCategoryKey, setBindingCategoryKey] =
        useState<DisplayCategoryKey | null>(null)
    const [editingDisplayCategory, setEditingDisplayCategory] =
        useState<ModelDisplayCategorySummary | null>(null)
    const [detailModelKey, setDetailModelKey] = useState<ModelKey | null>(null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)
    const [remarkSaving, setRemarkSaving] = useState(false)
    const [savingBinding, setSavingBinding] = useState(false)
    const [savingDisplayCategory, setSavingDisplayCategory] = useState(false)
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null)
    const [visibilityActionKey, setVisibilityActionKey] = useState<
        string | null
    >(null)

    const mappingRequestIdRef = useRef(0)

    const scope = useMemo(
        () =>
            activeProjectId !== null
                ? { scopeType: 'project' as const, projectId: activeProjectId }
                : { scopeType: 'platform' as const, projectId: null },
        [activeProjectId],
    )
    const modelVersionConfigLogModule =
        scope.scopeType === 'project'
            ? MODEL_VERSION_PROJECT_CONFIG_LOG_MODULE
            : MODEL_VERSION_PLATFORM_CONFIG_LOG_MODULE

    const projectOptions = useMemo<ScopeSelectorOption[]>(
        () =>
            projects.map((project) => ({
                label: t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.scope.projectOption`,
                    {
                        code: project.code,
                        name: project.name,
                        defaultValue: `${project.name} / ${project.code}`,
                    },
                ),
                value: encodeProjectScopeValue(project.id),
            })),
        [projects, t],
    )

    const availableProjectOptions = useMemo(() => {
        const projectConfigRecords =
            mode === 'types'
                ? projectTypeConfigRecords
                : projectModelConfigRecords
        const configuredProjectIds = new Set(
            projectConfigRecords.map((record) => record.projectId),
        )
        return projectOptions.filter((option) => {
            const parsed = parseScopeValue(option.value)
            return (
                parsed.scopeType === 'project' &&
                !configuredProjectIds.has(parsed.projectId)
            )
        })
    }, [
        mode,
        projectModelConfigRecords,
        projectOptions,
        projectTypeConfigRecords,
    ])

    const getDisplayCategoryLabel = useCallback(
        (categoryKey: string) => {
            const category = displayCategories.find(
                (item) => item.key === categoryKey,
            )
            const language = i18n.language.toLowerCase()
            if (category) {
                if (language.startsWith('en')) {
                    return category.display_name_en
                }
                if (language.includes('hk') || language.includes('hant')) {
                    return category.display_name_zh_hk
                }
                return category.display_name_zh_cn
            }
            return t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.displayCategories.${categoryKey}`,
                {
                    defaultValue: categoryKey,
                },
            )
        },
        [displayCategories, i18n.language, t],
    )

    const getModelKeyLabel = useCallback(
        (modelKey: ModelKey | string) =>
            t(`${MODEL_MANAGEMENT_I18N_PREFIX}.modelKeys.${modelKey}`, {
                defaultValue: modelKey,
            }),
        [t],
    )

    const getManualRecognitionTypeLabel = useCallback(
        (manualType: ManualRecognitionTypeSummary) => {
            const language = i18n.language.toLowerCase()
            const localizedName = language.startsWith('en')
                ? manualType.display_name_en
                : language.includes('hk') || language.includes('hant')
                  ? manualType.display_name_zh_hk
                  : manualType.display_name_zh_cn
            return `${localizedName} (${manualType.key})`
        },
        [i18n.language],
    )

    const getRawClassLabel = useCallback(
        (rawClass: string) => {
            const rawClassNorm = normalizeRawClass(rawClass)
            const labelKey = RAW_CLASS_LABEL_KEYS[rawClassNorm]
            if (!labelKey) {
                return rawClass
            }
            return t(`${MODEL_MANAGEMENT_I18N_PREFIX}.${labelKey}`, {
                defaultValue: rawClass,
            })
        },
        [t],
    )

    const formatRawClassLabel = useCallback(
        (rawClass: string): string => {
            const label = getRawClassLabel(rawClass)
            return label === rawClass ? rawClass : `${label} (${rawClass})`
        },
        [getRawClassLabel],
    )

    const toRawClassBindingOption = useCallback(
        (option: ModelRawClassOptionSummary): RawClassBindingOption => {
            const value = encodeBindingValue(option.raw_class_norm)
            return {
                value,
                label: option.display_name_zh
                    ? `${option.display_name_zh} (${option.raw_class})`
                    : formatRawClassLabel(option.raw_class),
                modelKeys: option.model_keys,
                rawClass: option.raw_class,
                rawClassNorm: option.raw_class_norm,
                displayCategoryKey: option.display_category_key,
                isMapped: option.is_mapped,
                isCurrentModel: option.is_current_model,
                isHistoricalOnly: option.is_historical_only,
                sourceVersions: option.source_versions,
            }
        },
        [formatRawClassLabel],
    )

    const loadProjects = useCallback(async () => {
        try {
            const projectData = await getModelManagementProjects()
            setProjects(projectData)
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.loadProjectsFailed`,
                    ),
                    t,
                ),
            )
        }
    }, [message, t])

    const toProjectConfigRecordRows = (
        records: Awaited<ReturnType<typeof getScopedModelProjectConfigRecords>>,
    ): ProjectConfigRecordRow[] =>
        records.map((record) => ({
            projectId: record.project_id,
            projectCode: record.project_code,
            projectName: record.project_name,
            record,
        }))

    const loadProjectModelConfigRecords = useCallback(async () => {
        setProjectConfigRecordsLoading(true)
        try {
            const records = await getScopedModelProjectConfigRecords('runtime')
            setProjectModelConfigRecords(toProjectConfigRecordRows(records))
        } catch (error) {
            setProjectModelConfigRecords([])
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.loadProjectConfigFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setProjectConfigRecordsLoading(false)
        }
    }, [message, t])

    const loadProjectTypeConfigRecords = useCallback(async () => {
        setProjectConfigRecordsLoading(true)
        try {
            const records = await getScopedModelProjectConfigRecords('mapping')
            setProjectTypeConfigRecords(toProjectConfigRecordRows(records))
        } catch (error) {
            setProjectTypeConfigRecords([])
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.loadProjectConfigFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setProjectConfigRecordsLoading(false)
        }
    }, [message, t])

    const loadProjectConfigRecords = useCallback(async () => {
        await Promise.all([
            loadProjectModelConfigRecords(),
            loadProjectTypeConfigRecords(),
        ])
    }, [loadProjectModelConfigRecords, loadProjectTypeConfigRecords])

    const loadVersions = useCallback(async () => {
        setLoading(true)
        try {
            const versionData = await getModelVersions()
            const runtimeConfigData = await getScopedModelRuntimeConfigs({
                effective: false,
                projectId: scope.projectId,
                scopeType: scope.scopeType,
            })
            setVersions(versionData)
            setRuntimeConfigs(runtimeConfigData)
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.loadFailed`),
                    t,
                ),
            )
        } finally {
            setLoading(false)
        }
    }, [message, scope.projectId, scope.scopeType, t])

    const loadMappings = useCallback(async () => {
        const requestId = mappingRequestIdRef.current + 1
        mappingRequestIdRef.current = requestId
        setMappingLoading(true)
        try {
            const [
                categories,
                mappings,
                rawOptions,
                manualTypes,
                scopedManualMappings,
            ] = await Promise.all([
                getModelDisplayCategories({
                    effective: true,
                    projectId: scope.projectId,
                    scopeType: scope.scopeType,
                }),
                getModelClassMappings(DEFAULT_MAPPING_SET_ID, {
                    effective: false,
                    projectId: scope.projectId,
                    scopeType: scope.scopeType,
                }),
                getModelRawClassOptions(DEFAULT_MAPPING_SET_ID, undefined, {
                    effective: false,
                    projectId: scope.projectId,
                    scopeType: scope.scopeType,
                }),
                getManualRecognitionTypes(),
                getManualRecognitionTypeMappings({
                    effective: false,
                    projectId: scope.projectId,
                    scopeType: scope.scopeType,
                }),
            ])
            if (requestId !== mappingRequestIdRef.current) {
                return
            }
            setDisplayCategories(categories)
            setClassMappings(mappings)
            setRawClassOptions(rawOptions.map(toRawClassBindingOption))
            setManualRecognitionTypes(manualTypes)
            setManualTypeMappings(scopedManualMappings)
        } catch (error) {
            if (requestId !== mappingRequestIdRef.current) {
                return
            }
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.loadMappingsFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            if (requestId === mappingRequestIdRef.current) {
                setMappingLoading(false)
            }
        }
    }, [message, scope.projectId, scope.scopeType, t, toRawClassBindingOption])

    useEffect(() => {
        void loadProjects()
    }, [loadProjects])

    useEffect(() => {
        void loadProjectConfigRecords()
    }, [loadProjectConfigRecords])

    useEffect(() => {
        void loadVersions()
    }, [loadVersions])

    useEffect(() => {
        if (recognitionTypeView === 'custom') {
            void loadMappings()
        }
    }, [loadMappings, recognitionTypeView])

    const versionById = useMemo(
        () =>
            new Map(
                versions
                    .filter(
                        (version) =>
                            version.id !== null && version.id !== undefined,
                    )
                    .map((version) => [Number(version.id), version]),
            ),
        [versions],
    )

    const currentVersionIdByModelKey = useMemo(
        () =>
            new Map(
                runtimeConfigs
                    .filter((config) => config.current_version_id)
                    .map((config) => [
                        config.model_key,
                        Number(config.current_version_id),
                    ]),
            ),
        [runtimeConfigs],
    )

    const modelRows = useMemo<ModelConfigurationRow[]>(
        () =>
            MODEL_KEY_OPTIONS.map((option) => {
                const runtimeConfig = runtimeConfigs.find(
                    (config) => config.model_key === option.value,
                )
                const currentVersion =
                    runtimeConfig?.current_version_id !== null &&
                    runtimeConfig?.current_version_id !== undefined
                        ? versionById.get(
                              Number(runtimeConfig.current_version_id),
                          )
                        : undefined
                return {
                    modelKey: option.value,
                    activeVersion: currentVersion,
                    runtimeConfig,
                    statusKey: getModelStatusKey(currentVersion),
                }
            }),
        [runtimeConfigs, versionById],
    )

    const activeClassOptionByValue = useMemo(
        () => new Map(rawClassOptions.map((option) => [option.value, option])),
        [rawClassOptions],
    )

    const manualRecognitionTypeByKey = useMemo(
        () =>
            new Map(
                manualRecognitionTypes.map((manualType) => [
                    manualType.key,
                    manualType,
                ]),
            ),
        [manualRecognitionTypes],
    )

    const getActiveMappingsForCategory = useCallback(
        (categoryKey: string) =>
            classMappings.filter(
                (mapping) =>
                    mapping.mapping_set_id === DEFAULT_MAPPING_SET_ID &&
                    mapping.display_category_key === categoryKey,
            ),
        [classMappings],
    )

    const getActiveManualMappingsForCategory = useCallback(
        (categoryKey: string) =>
            manualTypeMappings.filter((mapping) => {
                return (
                    mapping.display_category_key === categoryKey &&
                    manualRecognitionTypeByKey.has(mapping.manual_type_key)
                )
            }),
        [manualRecognitionTypeByKey, manualTypeMappings],
    )

    const bindingModalRawClassOptions = useMemo(() => {
        if (!bindingCategoryKey) {
            return []
        }
        const availableCategoryKeys = new Set(
            displayCategories.map((category) => category.key),
        )
        const boundValueSet = new Set(
            getActiveMappingsForCategory(bindingCategoryKey).map((mapping) =>
                encodeBindingValue(mapping.raw_class_norm),
            ),
        )
        return rawClassOptions.flatMap((option) => {
            const occupiedCategoryKey =
                option.isMapped &&
                option.displayCategoryKey &&
                availableCategoryKeys.has(option.displayCategoryKey) &&
                option.displayCategoryKey !== bindingCategoryKey
                    ? option.displayCategoryKey
                    : null
            if (occupiedCategoryKey) {
                return [
                    {
                        ...option,
                        disabled: true,
                        label: `${option.label} - ${t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.bindingModal.boundToCategory`,
                        )}`,
                    },
                ]
            }
            return isOperatorSelectableRawClassOption(
                option,
                boundValueSet,
                bindingCategoryKey,
                availableCategoryKeys,
            )
                ? [option]
                : []
        })
    }, [
        bindingCategoryKey,
        displayCategories,
        getActiveMappingsForCategory,
        rawClassOptions,
        t,
    ])

    const bindingModalManualTypeOptions = useMemo(() => {
        if (!bindingCategoryKey) return []
        return manualRecognitionTypes
            .filter((manualType) => {
                const mapping = manualTypeMappings.find(
                    (item) => item.manual_type_key === manualType.key,
                )
                return (
                    !mapping ||
                    mapping.display_category_key === bindingCategoryKey
                )
            })
            .map((manualType) => ({
                label: getManualRecognitionTypeLabel(manualType),
                value: manualType.key,
            }))
    }, [
        bindingCategoryKey,
        getManualRecognitionTypeLabel,
        manualRecognitionTypes,
        manualTypeMappings,
    ])

    const openDisplayCategoryModal = (
        category?: ModelDisplayCategorySummary,
    ) => {
        setEditingDisplayCategory(category ?? null)
        setDisplayCategoryModalOpen(true)
    }

    const closeUploadModal = () => {
        setUploadModalOpen(false)
        setSelectedFile(null)
        uploadForm.resetFields()
    }

    const openUploadModal = () => {
        uploadForm.resetFields()
        setSelectedFile(null)
        setUploadModalOpen(true)
    }

    const openBindingModal = (categoryKey: DisplayCategoryKey) => {
        setBindingCategoryKey(categoryKey)
        setBindingModalOpen(true)
    }

    useEffect(() => {
        if (!displayCategoryModalOpen) {
            return
        }
        displayCategoryForm.resetFields()
        displayCategoryForm.setFieldsValue({
            displayNameZhCn: editingDisplayCategory?.display_name_zh_cn ?? '',
            color: editingDisplayCategory?.color ?? '#13c2c2',
            syncToProjects: undefined,
        })
    }, [
        displayCategoryForm,
        displayCategoryModalOpen,
        editingDisplayCategory,
        scope.scopeType,
    ])

    useEffect(() => {
        if (!bindingModalOpen || !bindingCategoryKey) {
            return
        }
        bindingForm.setFieldsValue({
            boundRawClasses: getActiveMappingsForCategory(
                bindingCategoryKey,
            ).map((mapping) => encodeBindingValue(mapping.raw_class_norm)),
            boundManualTypes: getActiveManualMappingsForCategory(
                bindingCategoryKey,
            ).map((mapping) => mapping.manual_type_key),
        })
    }, [
        bindingCategoryKey,
        bindingForm,
        bindingModalOpen,
        getActiveMappingsForCategory,
        getActiveManualMappingsForCategory,
    ])

    const openDetailModal = (
        modelKey: ModelKey,
        logContext?: ModelVersionDetailLogContext | null,
    ) => {
        if (logContext !== undefined) {
            setDetailLogContext(logContext)
        } else if (!detailLogContext) {
            setDetailLogContext(
                getModelVersionDetailLogContext(
                    modelVersionConfigLogModule,
                    MODEL_VERSION_CHANGE_CURRENT_LOG_ACTION,
                ),
            )
        }
        setDetailModelKey(modelKey)
        setDetailModalOpen(true)
    }

    const handleScopeTabChange = (value: ModelScopeTabKey) => {
        setScopeTab(value)
        if (value === 'platform') {
            setActiveProjectId(null)
            setProjectConfigDetailOpen(false)
            setProjectBindingDetailOpen(false)
            setDetailLogContext(null)
        }
    }

    const handleRecognitionTypeViewChange = (value: string) => {
        handleScopeTabChange('platform')
        setRecognitionTypeView(value as RecognitionTypeView)
    }

    const openProjectConfigModal = () => {
        setProjectConfigModalValue(availableProjectOptions[0]?.value)
        setProjectConfigModalOpen(true)
    }

    const filteredProjectModelConfigRecords = useMemo(() => {
        const keyword = appliedProjectSearch.trim().toLowerCase()
        if (!keyword) {
            return projectModelConfigRecords
        }
        return projectModelConfigRecords.filter((row) =>
            `${row.projectName} ${row.projectCode}`
                .toLowerCase()
                .includes(keyword),
        )
    }, [appliedProjectSearch, projectModelConfigRecords])

    const filteredProjectTypeConfigRecords = useMemo(() => {
        const keyword = appliedTypeProjectSearch.trim().toLowerCase()
        if (!keyword) {
            return projectTypeConfigRecords
        }
        return projectTypeConfigRecords.filter((row) =>
            `${row.projectName} ${row.projectCode}`
                .toLowerCase()
                .includes(keyword),
        )
    }, [appliedTypeProjectSearch, projectTypeConfigRecords])

    const resetProjectSearch = () => {
        setProjectSearch('')
        setAppliedProjectSearch('')
    }

    const applyProjectSearch = () => {
        setAppliedProjectSearch(projectSearch)
    }

    const resetTypeProjectSearch = () => {
        setTypeProjectSearch('')
        setAppliedTypeProjectSearch('')
    }

    const applyTypeProjectSearch = () => {
        setAppliedTypeProjectSearch(typeProjectSearch)
    }

    const selectedProjectIdValues = useMemo(
        () => selectedProjectIds.map(Number).filter(Number.isFinite),
        [selectedProjectIds],
    )

    const selectedUsageRows = useMemo(
        () =>
            usageRows.filter((row) =>
                selectedUsageConfigKeys.some(
                    (key) => String(key) === row.usageKey,
                ),
            ),
        [selectedUsageConfigKeys, usageRows],
    )

    const selectedRuntimeConfigTargets = useMemo(
        () =>
            usageVersion
                ? selectedUsageRows.map((row) => ({
                      projectId: row.projectId,
                      scopeType: row.scopeType,
                  }))
                : selectedProjectIdValues.map((projectId) => ({
                      projectId,
                      scopeType: 'project' as const,
                  })),
        [selectedProjectIdValues, selectedUsageRows, usageVersion],
    )

    const runBatchRuntimeAction = async (
        action: 'pause' | 'resume',
        modelKey: ModelKey,
    ) => {
        const directAction = action === 'resume' ? '批量恢复' : '批量暂停'
        const operationAction = usageVersion
            ? getUploadUsageLogAction(directAction)
            : directAction
        setBatchLoading(true)
        try {
            const results = await Promise.allSettled(
                selectedRuntimeConfigTargets.map((target) =>
                    updateModelRuntimeConfig({
                        modelKey,
                        operationModule: usageVersion
                            ? MODEL_VERSION_UPLOAD_LOG_MODULE
                            : getRuntimeConfigLogModule(target.scopeType),
                        operationAction,
                        participateInFusion: action === 'resume',
                        participateInInference: action === 'resume',
                        projectId: target.projectId,
                        scopeType: target.scopeType,
                    }),
                ),
            )
            const failed = results.filter(
                (result) => result.status === 'rejected',
            ).length
            if (usageVersion) {
                const succeededTargets = results.flatMap((result, index) =>
                    result.status === 'fulfilled'
                        ? [selectedRuntimeConfigTargets[index]]
                        : [],
                )
                setUsageRows((current) =>
                    current.map((row) =>
                        succeededTargets.some(
                            (target) =>
                                target.scopeType === row.scopeType &&
                                target.projectId === row.projectId,
                        )
                            ? {
                                  ...row,
                                  participateInInference: action === 'resume',
                              }
                            : row,
                    ),
                )
            }
            message[failed ? 'warning' : 'success'](
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.batchResult`, {
                    failed,
                    success: results.length - failed,
                }),
            )
            await Promise.all([loadVersions(), loadProjectModelConfigRecords()])
            if (!failed) {
                setSelectedProjectIds([])
                setSelectedUsageConfigKeys([])
            }
        } finally {
            setBatchLoading(false)
        }
    }

    const handleBatchAction = (
        action: 'change' | 'pause' | 'resume' | 'delete',
    ) => {
        if (action !== 'delete') {
            setBatchOperation(action)
            setBatchTargetVersionId(undefined)
            setBatchChangeOpen(true)
            return
        }
        const execute = async () => {
            setBatchLoading(true)
            try {
                const results = await Promise.allSettled(
                    selectedProjectIdValues.map((projectId) =>
                        deleteModelProjectConfig(projectId, '批量删除'),
                    ),
                )
                const failed = results.filter(
                    (result) => result.status === 'rejected',
                ).length
                message[failed ? 'warning' : 'success'](
                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.batchResult`, {
                        failed,
                        success: results.length - failed,
                    }),
                )
                await loadProjectModelConfigRecords()
                if (!failed) setSelectedProjectIds([])
            } finally {
                setBatchLoading(false)
            }
        }
        modal.confirm({
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.batchConfirm.${action}Title`,
                {
                    count: selectedProjectIdValues.length,
                },
            ),
            content: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.batchConfirm.${action}Content`,
            ),
            maskClosable: false,
            okButtonProps: { danger: true },
            okText: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.confirm`),
            onOk: execute,
        })
    }

    const handleBatchChange = async () => {
        const targetModelKey = usageVersion?.model_key ?? batchModelKey
        if (batchOperation !== 'change') {
            await runBatchRuntimeAction(batchOperation, targetModelKey)
            setBatchChangeOpen(false)
            return
        }
        if (!batchTargetVersionId) return
        const operationAction = usageVersion
            ? getUploadUsageLogAction('批量更换')
            : '批量更换'
        setBatchLoading(true)
        try {
            const results = await Promise.allSettled(
                selectedRuntimeConfigTargets.map(async (target) => {
                    if (target.scopeType === 'platform') {
                        const targetVersion = versionById.get(
                            batchTargetVersionId,
                        )
                        if (targetVersion?.status === 'disabled') {
                            return rollbackModelVersion(batchTargetVersionId, {
                                operationModule:
                                    usageVersion
                                        ? MODEL_VERSION_UPLOAD_LOG_MODULE
                                        : getRuntimeConfigLogModule(
                                              target.scopeType,
                                          ),
                                operationAction,
                            })
                        }
                        return activateModelVersion(batchTargetVersionId, {
                            operationModule:
                                usageVersion
                                    ? MODEL_VERSION_UPLOAD_LOG_MODULE
                                    : getRuntimeConfigLogModule(target.scopeType),
                            operationAction,
                        })
                    }
                    return updateModelRuntimeConfig({
                        currentVersionId: batchTargetVersionId,
                        modelKey: targetModelKey,
                        operationModule: usageVersion
                            ? MODEL_VERSION_UPLOAD_LOG_MODULE
                            : getRuntimeConfigLogModule(target.scopeType),
                        operationAction,
                        projectId: target.projectId,
                        scopeType: target.scopeType,
                    })
                }),
            )
            const failed = results.filter(
                (result) => result.status === 'rejected',
            ).length
            message[failed ? 'warning' : 'success'](
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.batchResult`, {
                    failed,
                    success: results.length - failed,
                }),
            )
            setBatchChangeOpen(false)
            if (usageVersion) {
                await openVersionUsage(usageVersion)
            }
            if (!failed) {
                setSelectedProjectIds([])
                setSelectedUsageConfigKeys([])
            }
        } finally {
            setBatchLoading(false)
        }
    }

    async function openVersionUsage(version: ModelVersionSummary) {
        if (!version.id) return
        setUsageVersion(version)
        setBatchModelKey(version.model_key)
        setSelectedProjectIds([])
        setSelectedUsageConfigKeys([])
        setDetailLogContext(null)
        setUsageLoading(true)
        setUsageSearch('')
        setUsageRows([])
        setUsagePlatformInUse(false)
        try {
            const [records, platformConfigs] = await Promise.all([
                getScopedModelProjectConfigRecords('runtime'),
                getScopedModelRuntimeConfigs({
                    effective: false,
                    projectId: null,
                    scopeType: 'platform',
                }),
            ])
            const platformConfig = platformConfigs.find(
                (config) =>
                    config.model_key === version.model_key &&
                    config.current_version_id === version.id,
            )
            setUsagePlatformInUse(Boolean(platformConfig))
            const matched: Array<ModelUsageConfigRow | null> = await Promise.all(
                toProjectConfigRecordRows(records).map(async (row) => {
                    const configs = await getScopedModelRuntimeConfigs({
                        effective: false,
                        projectId: row.projectId,
                        scopeType: 'project',
                    })
                    const matchingConfig = configs.find(
                        (config) =>
                            config.model_key === version.model_key &&
                            config.current_version_id === version.id,
                    )
                    return matchingConfig
                        ? {
                              usageKey: `project:${row.projectId}`,
                              scopeType: 'project' as const,
                              projectId: row.projectId,
                              projectCode: row.projectCode,
                              projectName: row.projectName,
                              participateInInference:
                                  matchingConfig.participate_in_inference,
                          }
                        : null
                }),
            )
            const projectUsageRows = matched.filter(
                (row): row is ModelUsageConfigRow => row !== null,
            )
            setUsageRows([
                ...(platformConfig
                    ? [
                          {
                              usageKey: 'platform',
                              scopeType: 'platform' as const,
                              projectId: null,
                              projectCode: '-',
                              projectName: t(
                                  `${MODEL_MANAGEMENT_I18N_PREFIX}.scope.platformDefault`,
                              ),
                              participateInInference:
                                  platformConfig.participate_in_inference,
                          },
                      ]
                    : []),
                ...projectUsageRows,
            ])
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.loadUsageFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setUsageLoading(false)
        }
    }

    const filteredUsageRows = useMemo(() => {
        const keyword = usageSearch.trim().toLowerCase()
        return keyword
            ? usageRows.filter((row) =>
                  `${row.projectName} ${row.projectCode}`
                      .toLowerCase()
                      .includes(keyword),
              )
            : usageRows
    }, [usageRows, usageSearch])

    const handleUsageRuntimeAction = async (
        row: ModelUsageConfigRow,
        participateInInference: boolean,
    ) => {
        if (!usageVersion) return
        await updateModelRuntimeConfig({
            modelKey: usageVersion.model_key,
            operationModule: MODEL_VERSION_UPLOAD_LOG_MODULE,
            operationAction: getUploadUsageLogAction(
                participateInInference ? '恢复' : '暂停',
            ),
            participateInFusion: participateInInference,
            participateInInference,
            projectId: row.projectId,
            scopeType: row.scopeType,
        })
        setUsageRows((current) =>
            current.map((item) =>
                item.usageKey === row.usageKey
                    ? { ...item, participateInInference }
                    : item,
            ),
        )
        message.success(
            t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.runtimeConfigSaved`),
        )
    }

    const openUsageConfigDetail = (row: ModelUsageConfigRow) => {
        const logContext = getModelVersionDetailLogContext(
            MODEL_VERSION_UPLOAD_LOG_MODULE,
            getUploadUsageLogAction('更换'),
        )
        if (!usageVersion) {
            return
        }
        setActiveProjectId(row.projectId)
        setProjectConfigDetailOpen(false)
        setProjectBindingDetailOpen(false)
        openDetailModal(usageVersion.model_key, logContext)
    }

    const openProjectModelConfigDetail = async (
        projectId: number,
        logContext: ModelVersionDetailLogContext | null = null,
    ) => {
        setDetailLogContext(logContext)
        setActiveProjectId(projectId)
        setProjectBindingDetailOpen(false)
        setProjectConfigDetailOpen(true)
    }

    const openProjectBindingDetail = async (projectId: number) => {
        setActiveProjectId(projectId)
        setProjectConfigDetailOpen(false)
        setProjectBindingDetailOpen(true)
    }

    const closeProjectModelConfigDetail = () => {
        setProjectConfigDetailOpen(false)
        setActiveProjectId(null)
        setDetailLogContext(null)
    }

    const closeProjectBindingDetail = () => {
        setProjectBindingDetailOpen(false)
        setActiveProjectId(null)
    }

    const handleCreateProjectConfig = async () => {
        const targetProjectId = projectConfigModalValue
            ? parseScopeValue(projectConfigModalValue).projectId
            : null
        if (targetProjectId === null) {
            message.warning(
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.projectRequired`),
            )
            return
        }
        setCreatingProjectConfig(true)
        try {
            if (mode === 'types') {
                await createModelProjectTypeConfig(targetProjectId)
            } else {
                await createModelProjectConfig(targetProjectId)
            }
            setScopeTab('project')
            setProjectConfigModalOpen(false)
            setProjectConfigModalValue(undefined)
            if (mode === 'types') {
                await loadProjectTypeConfigRecords()
            } else {
                await loadProjectModelConfigRecords()
            }
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.createProjectConfigSuccess`,
                ),
            )
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.createProjectConfigFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setCreatingProjectConfig(false)
        }
    }

    const handleDeleteProjectConfig = async (
        projectId: number,
        configKind: 'runtime' | 'mapping',
    ) => {
        setDeletingProjectConfig(true)
        try {
            if (configKind === 'mapping') {
                await deleteModelProjectTypeConfig(projectId)
            } else {
                await deleteModelProjectConfig(projectId)
            }
            if (activeProjectId === projectId) {
                setActiveProjectId(null)
                if (configKind === 'runtime') {
                    setProjectConfigDetailOpen(false)
                    setRuntimeConfigs([])
                } else {
                    setProjectBindingDetailOpen(false)
                    setClassMappings([])
                }
            }
            if (configKind === 'mapping') {
                await loadProjectTypeConfigRecords()
            } else {
                await loadProjectModelConfigRecords()
            }
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.deleteProjectConfigSuccess`,
                ),
            )
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.deleteProjectConfigFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setDeletingProjectConfig(false)
        }
    }

    const handleUpload = async () => {
        const values = await uploadForm.validateFields()
        if (!selectedFile) {
            message.error(
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.fileRequired`),
            )
            return
        }
        setUploading(true)
        try {
            const parsed = await parseModelPackage(selectedFile)
            await uploadModelVersion({
                modelKey: parsed.modelKey,
                versionLabel: parsed.versionLabel,
                remark: values.remark?.trim() || undefined,
                modelKind: 'detect',
                file: parsed.modelFile,
                modelLabels: parsed.labels,
            })
            notification.success({
                description: t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.uploadValidatedSuccess`,
                ),
                duration: 0,
                key: 'model-upload-success',
                message: t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.uploadSuccessTitle`,
                ),
                placement: 'top',
            })
            closeUploadModal()
            await Promise.all([loadVersions(), loadMappings()])
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.uploadFailed`),
                    t,
                ),
            )
        } finally {
            setUploading(false)
        }
    }

    const openRemarkModal = (version: ModelVersionSummary) => {
        setRemarkVersion(version)
        remarkForm.setFieldsValue({ remark: version.remark ?? '' })
    }

    const closeRemarkModal = () => {
        setRemarkVersion(null)
        remarkForm.resetFields()
    }

    const handleRemarkSave = async () => {
        if (!remarkVersion?.id) {
            return
        }
        const values = await remarkForm.validateFields()
        setRemarkSaving(true)
        try {
            const updated = await updateModelVersionRemark(remarkVersion.id, {
                remark: values.remark?.trim() || null,
            })
            setVersions((current) =>
                current.map((version) =>
                    version.id === updated.id ? updated : version,
                ),
            )
            message.success(
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.remarkSaveSuccess`),
            )
            closeRemarkModal()
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.remarkSaveFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setRemarkSaving(false)
        }
    }

    const handleEnableVersion = async (version: ModelVersionSummary) => {
        const versionId = version.id
        if (versionId === null || versionId === undefined) {
            return
        }
        setActionLoadingId(versionId)
        const operationModule =
            detailLogContext?.operationModule ?? modelVersionConfigLogModule
        const operationAction =
            detailLogContext?.changeAction ??
            `${MODEL_VERSION_CHANGE_CURRENT_LOG_ACTION}-${MODEL_VERSION_SET_CURRENT_LOG_ACTION}`
        try {
            if (scope.scopeType === 'platform') {
                if (version.status === 'disabled') {
                    await rollbackModelVersion(versionId, {
                        operationModule,
                        operationAction,
                    })
                } else {
                    await activateModelVersion(versionId, {
                        operationModule,
                        operationAction,
                    })
                }
            } else {
                await updateModelRuntimeConfig({
                    currentVersionId: versionId,
                    modelKey: version.model_key,
                    operationModule,
                    operationAction,
                    participateInFusion: true,
                    participateInInference: true,
                    projectId: scope.projectId,
                    scopeType: scope.scopeType,
                    visibleToUser: true,
                })
            }
            message.success(
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.enableSuccess`),
            )
            await Promise.all([loadVersions(), loadMappings()])
            if (usageVersion) {
                await openVersionUsage(usageVersion)
            }
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.enableFailed`),
                    t,
                ),
            )
        } finally {
            setActionLoadingId(null)
        }
    }

    const handleDeleteVersion = async (
        version: ModelVersionSummary,
        operationModule = MODEL_VERSION_UPLOAD_LOG_MODULE,
    ) => {
        const versionId = version.id
        if (versionId === null || versionId === undefined) {
            return
        }
        setActionLoadingId(versionId)
        try {
            await deleteModelVersion(versionId, {
                operationModule,
                operationAction: MODEL_VERSION_DELETE_LOG_ACTION,
            })
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.deleteVersionSuccess`,
                ),
            )
            await Promise.all([loadVersions(), loadMappings()])
        } catch (error) {
            const detail = getModelManagementErrorDetail(
                error,
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.deleteVersionFailed`,
                ),
                t,
            )
            if (detail.includes('全局标注模型配置')) {
                message.warning(
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.deleteVersionAnnotationInUse`,
                    ),
                )
            } else if (
                isModelVersionDeleteInUseError(detail) ||
                detail.includes('该模型正在被以下配置使用')
            ) {
                await openVersionUsage(version)
                message.warning(
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.deleteVersionInUse`,
                    ),
                )
            } else {
                message.error(detail)
            }
        } finally {
            setActionLoadingId(null)
        }
    }

    const handleRuntimeConfigUpdate = async (
        modelKey: ModelKey,
        payload: {
            participateInInference?: boolean
            participateInFusion?: boolean
        },
    ) => {
        const usingUsageLogContext =
            detailLogContext?.changeAction.startsWith(
                MODEL_VERSION_UPLOAD_USAGE_ACTION_PREFIX,
            ) ?? false
        const toggleAction =
            payload.participateInInference === undefined
                ? undefined
                : payload.participateInInference
                  ? '恢复识别'
                  : '暂停识别'
        try {
            await updateModelRuntimeConfig({
                modelKey,
                operationModule:
                    detailLogContext?.operationModule ?? modelVersionConfigLogModule,
                ...(toggleAction
                    ? {
                          operationAction: usingUsageLogContext
                              ? getUploadUsageLogAction(
                                    toggleAction.replace('识别', ''),
                                )
                              : toggleAction,
                      }
                    : {}),
                projectId: scope.projectId,
                scopeType: scope.scopeType,
                ...payload,
            })
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.runtimeConfigSaved`,
                ),
            )
            await Promise.all([loadVersions(), loadMappings()])
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.runtimeConfigFailed`,
                    ),
                    t,
                ),
            )
        }
    }

    const handleDisplayCategorySubmit = async () => {
        const values = await displayCategoryForm
            .validateFields()
            .catch(() => null)
        if (!values) {
            return
        }
        const displayNameZhCn = values.displayNameZhCn?.trim()
        const color = values.color?.trim().toLowerCase()
        if (!displayNameZhCn || !color) {
            return
        }
        setSavingDisplayCategory(true)
        try {
            if (editingDisplayCategory) {
                const updatePayload = {
                    displayNameZhCn,
                    color,
                    ...(scope.scopeType === 'project'
                        ? {
                              scopeType: scope.scopeType,
                              projectId: scope.projectId,
                          }
                        : values.syncToProjects !== undefined
                          ? { syncToProjects: values.syncToProjects }
                          : {}),
                }
                await updateModelDisplayCategory(
                    editingDisplayCategory.key,
                    updatePayload,
                )
            } else {
                const createPayload = {
                    displayNameZhCn,
                    color,
                    ...(scope.scopeType === 'project'
                        ? {
                              scopeType: scope.scopeType,
                              projectId: scope.projectId,
                          }
                        : {
                              syncToProjects: values.syncToProjects,
                          }),
                }
                await createModelDisplayCategory(createPayload)
            }
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.categorySaveSuccess`,
                ),
            )
            setDisplayCategoryModalOpen(false)
            setEditingDisplayCategory(null)
            await loadMappings()
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.categorySaveFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setSavingDisplayCategory(false)
        }
    }

    const handleDeleteDisplayCategory = async (
        category: ModelDisplayCategorySummary,
    ) => {
        try {
            if (scope.scopeType === 'project') {
                await deleteModelDisplayCategory(category.key, {
                    scopeType: scope.scopeType,
                    projectId: scope.projectId,
                })
            } else {
                await deleteModelDisplayCategory(category.key)
            }
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.categoryDeleteSuccess`,
                ),
            )
            await loadMappings()
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.categoryDeleteFailed`,
                    ),
                    t,
                ),
            )
        }
    }

    const handleDisplayCategoryVisibilityUpdate = async (
        category: ModelDisplayCategorySummary,
        visibleToUser: boolean,
    ) => {
        setVisibilityActionKey(category.key)
        try {
            await updateModelDisplayCategoryVisibility(category.key, {
                projectId: scope.projectId,
                scopeType: scope.scopeType,
                visibleToUser,
            })
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.categoryVisibilitySaveSuccess`,
                ),
            )
            await loadMappings()
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.categoryVisibilitySaveFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setVisibilityActionKey(null)
        }
    }

    const handleBindingSubmit = async () => {
        if (!bindingCategoryKey) {
            return
        }
        const values = await bindingForm.validateFields()
        const selectedValues = Array.isArray(values.boundRawClasses)
            ? values.boundRawClasses
            : []
        const selectedManualTypeKeys = Array.isArray(values.boundManualTypes)
            ? values.boundManualTypes
            : []
        const selectedOptions = rawClassOptions.filter((option) =>
            selectedValues.includes(option.value),
        )

        setSavingBinding(true)
        try {
            await replaceModelDisplayCategoryBindings(bindingCategoryKey, {
                manualTypeKeys: selectedManualTypeKeys,
                projectId: scope.projectId,
                rawClassNorms: selectedOptions.map((option) => option.rawClassNorm),
                scopeType: scope.scopeType,
            })
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.bindingSaveSuccess`,
                ),
            )
            setBindingModalOpen(false)
            setBindingCategoryKey(null)
            bindingForm.resetFields()
            await loadMappings()
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.bindingSaveFailed`,
                    ),
                    t,
                ),
            )
        } finally {
            setSavingBinding(false)
        }
    }

    const renderModelKey = (modelKey: ModelKey | string) => (
        <Tag>{getModelKeyLabel(modelKey)}</Tag>
    )

    const renderConfigurationStatus = (
        statusKey: ModelConfigurationStatusKey,
        record: ModelConfigurationRow,
    ) => (
        <Space size={4} wrap>
            <Tag color={MODEL_CONFIGURATION_STATUS_COLORS[statusKey]}>
                {t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.configurationStatus.${statusKey}`,
                )}
            </Tag>
            {record.runtimeConfig?.is_inherited ? (
                <Tag>
                    {t(`${MODEL_MANAGEMENT_I18N_PREFIX}.scope.inherited`)}
                </Tag>
            ) : null}
        </Space>
    )

    const renderDisplayCategoryVisibility = (
        category: ModelDisplayCategorySummary,
    ) => {
        const visible = category.visible_to_user !== false
        return (
            <Tag color={visible ? 'success' : 'warning'}>
                {t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.displayVisibility.${visible ? 'visible' : 'hidden'}`,
                )}
            </Tag>
        )
    }

    const getVersionStatusKey = (
        version: ModelVersionSummary,
    ): ModelSelectionStatusKey => {
        const currentVersionId =
            version.model_key === 'mosquito'
                ? currentVersionIdByModelKey.get('mosquito')
                : currentVersionIdByModelKey.get('insect')
        return currentVersionId === version.id ? 'selected' : 'unselected'
    }

    const renderVersionStatus = (version: ModelVersionSummary) => {
        const statusKey = getVersionStatusKey(version)
        return (
            <Tag color={MODEL_SELECTION_STATUS_COLORS[statusKey]}>
                {t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.selectionStatus.${statusKey}`,
                )}
            </Tag>
        )
    }

    const renderModelFileName = (
        version: ModelVersionSummary,
        fallback: React.ReactNode = '-',
    ) =>
        renderPaginatedListText(version.artifact?.original_filename || fallback)

    const renderBoundMappings = (categoryKey: string) => {
        const mappings = getActiveMappingsForCategory(categoryKey)
        const manualMappings = getActiveManualMappingsForCategory(categoryKey)
        if (mappings.length === 0 && manualMappings.length === 0) {
            return (
                <span>
                    {t(`${MODEL_MANAGEMENT_I18N_PREFIX}.empty.noBindings`)}
                </span>
            )
        }
        return (
            <Space size={4} wrap>
                {mappings.map((mapping) => {
                    const value = encodeBindingValue(mapping.raw_class_norm)
                    const option = activeClassOptionByValue.get(value)
                    const label =
                        option?.label ??
                        formatRawClassLabel(mapping.raw_class_norm)
                    return (
                        <Tag
                            key={`${value}-${mapping.source_scope_type || 'local'}`}
                        >
                            {label}
                            {mapping.is_inherited
                                ? ` / ${t(`${MODEL_MANAGEMENT_I18N_PREFIX}.scope.inherited`)}`
                                : ''}
                        </Tag>
                    )
                })}
                {manualMappings.map((mapping) => {
                    const manualType = manualRecognitionTypeByKey.get(
                        mapping.manual_type_key,
                    )
                    if (!manualType) {
                        return null
                    }
                    return (
                        <Tag
                            key={`manual-${mapping.manual_type_key}-${mapping.source_scope_type || 'local'}`}
                        >
                            <Space size={6}>
                                <span
                                    aria-hidden="true"
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: manualType.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>
                                    {getManualRecognitionTypeLabel(manualType)}
                                    {mapping.is_inherited
                                        ? ` / ${t(`${MODEL_MANAGEMENT_I18N_PREFIX}.scope.inherited`)}`
                                        : ''}
                                </span>
                            </Space>
                        </Tag>
                    )
                })}
            </Space>
        )
    }

    const configurationColumns: TableColumnsType<ModelConfigurationRow> = [
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.configurationColumns.model`,
            ),
            dataIndex: 'modelKey',
            key: 'modelKey',
            width: 110,
            render: renderModelKey,
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.configurationColumns.status`,
            ),
            dataIndex: 'statusKey',
            key: 'statusKey',
            width: 120,
            render: (_value, record) =>
                renderConfigurationStatus(record.statusKey, record),
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.configurationColumns.currentVersion`,
            ),
            key: 'currentVersion',
            width: 260,
            render: (_value, record) => {
                const activeVersion = record.activeVersion
                if (!activeVersion) {
                    return t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.empty.noActiveModel`,
                    )
                }
                return (
                    <Space
                        direction="vertical"
                        size={0}
                        style={{ maxWidth: '100%', minWidth: 0 }}
                    >
                        {renderPaginatedListText(activeVersion.version_label)}
                        <span>
                            {localeService.formatDateTime(
                                getVersionUploadedAt(activeVersion),
                            )}
                        </span>
                    </Space>
                )
            },
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.configurationColumns.visibility`,
            ),
            key: 'visibility',
            width: 140,
            render: (_value, record) => {
                const config = record.runtimeConfig
                if (!record.activeVersion) {
                    return (
                        <Tooltip
                            title={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.runtimeNotes.unset`,
                            )}
                        >
                            <Tag>
                                {t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.runtimeFlags.unset`,
                                )}
                            </Tag>
                        </Tooltip>
                    )
                }
                const inferenceEnabled =
                    config?.participate_in_inference !== false
                return (
                    <Space size={4} wrap>
                        <Tooltip
                            title={
                                inferenceEnabled
                                    ? undefined
                                    : t(
                                          `${MODEL_MANAGEMENT_I18N_PREFIX}.runtimeNotes.pauseInference`,
                                      )
                            }
                        >
                            <Tag
                                color={inferenceEnabled ? 'success' : 'warning'}
                            >
                                {t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.runtimeFlags.inference.${inferenceEnabled ? 'on' : 'off'}`,
                                )}
                            </Tag>
                        </Tooltip>
                    </Space>
                )
            },
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.configurationColumns.actions`,
            ),
            key: 'actions',
            fixed: 'right',
            width: 320,
            render: (_value, record) => {
                const config = record.runtimeConfig
                const inferenceEnabled =
                    config?.participate_in_inference !== false
                return (
                    <Space size={4} wrap>
                        <Button
                            onClick={() => openDetailModal(record.modelKey)}
                            type="link"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.configureCurrent`,
                            )}
                        </Button>
                        {canActivate && record.activeVersion ? (
                            <Tooltip
                                title={t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.runtimeNotes.pauseInference`,
                                )}
                            >
                                <Button
                                    onClick={() =>
                                        void handleRuntimeConfigUpdate(
                                            record.modelKey,
                                            {
                                                participateInFusion:
                                                    !inferenceEnabled,
                                                participateInInference:
                                                    !inferenceEnabled,
                                            },
                                        )
                                    }
                                    type="link"
                                >
                                    {t(
                                        `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.${inferenceEnabled ? 'stopInference' : 'startInference'}`,
                                    )}
                                </Button>
                            </Tooltip>
                        ) : null}
                    </Space>
                )
            },
        },
    ]

    const versionColumns: TableColumnsType<ModelVersionSummary> = [
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.versionColumns.model`),
            dataIndex: 'model_key',
            key: 'modelKey',
            width: 100,
            render: renderModelKey,
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.versionColumns.version`),
            dataIndex: 'version_label',
            key: 'version',
            width: 180,
            ellipsis: true,
            render: renderPaginatedListText,
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.versionColumns.file`),
            key: 'file',
            width: 260,
            render: (_value, record) => (
                <Space
                    direction="vertical"
                    size={0}
                    style={{ maxWidth: '100%', minWidth: 0 }}
                >
                    {renderModelFileName(record)}
                    <span>{formatBytes(record.artifact?.size_bytes)}</span>
                </Space>
            ),
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.versionColumns.remark`),
            dataIndex: 'remark',
            key: 'remark',
            width: 240,
            render: (value: string | null) =>
                renderPaginatedListText(value || '-'),
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.versionColumns.uploadedAt`,
            ),
            key: 'uploadedAt',
            width: 160,
            render: (_value, record) =>
                localeService.formatDateTime(
                    record.artifact?.created_at || record.created_at,
                ),
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.versionColumns.actions`),
            key: 'actions',
            fixed: 'right',
            width: 280,
            render: (_value, record) =>
                (canUpload || canActivate) && record.id ? (
                    <Space size={4}>
                        {canUpload ? (
                            <Button
                                aria-label={t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.editRemark`,
                                )}
                                icon={<EditOutlined />}
                                onClick={() => openRemarkModal(record)}
                                style={{ paddingInline: 6 }}
                                type="link"
                            >
                                {t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.editRemark`,
                                )}
                            </Button>
                        ) : null}
                        {canActivate ? (
                            <>
                                <Button
                                    onClick={() =>
                                        void openVersionUsage(record)
                                    }
                                    style={{ paddingInline: 6 }}
                                    type="link"
                                >
                                    {t(
                                        `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.usage`,
                                    )}
                                </Button>
                                <NonDismissiblePopconfirm
                                    title={t(
                                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.confirmDeleteVersion`,
                                    )}
                                    onConfirm={() =>
                                        void handleDeleteVersion(
                                            record,
                                            MODEL_VERSION_UPLOAD_LOG_MODULE,
                                        )
                                    }
                                >
                                    <Button
                                        danger
                                        disabled={actionLoadingId === record.id}
                                        icon={<DeleteOutlined />}
                                        style={{ paddingInline: 6 }}
                                        type="link"
                                    >
                                        {t(
                                            `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.deleteVersion`,
                                        )}
                                    </Button>
                                </NonDismissiblePopconfirm>
                            </>
                        ) : null}
                    </Space>
                ) : null,
        },
    ]

    const bindingRows = useMemo<DisplayCategoryRow[]>(
        () =>
            displayCategories.map((category) => ({
                category,
                key: category.key,
            })),
        [displayCategories],
    )

    const bindingColumns: TableColumnsType<DisplayCategoryRow> = [
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.bindingColumns.category`),
            dataIndex: 'key',
            key: 'category',
            width: 150,
            render: (categoryKey: DisplayCategoryKey) => {
                const categoryLabel = getDisplayCategoryLabel(categoryKey)
                return (
                    <Tag color="blue" style={{ maxWidth: '100%' }}>
                        {renderPaginatedListText(categoryLabel)}
                    </Tag>
                )
            },
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.bindingColumns.boundClasses`,
            ),
            dataIndex: 'key',
            key: 'boundClasses',
            render: (categoryKey: DisplayCategoryKey) =>
                renderBoundMappings(categoryKey),
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.bindingColumns.color`),
            key: 'color',
            width: 120,
            render: (_value, record) => (
                <span
                    aria-label={record.category.color}
                    style={{
                        background: record.category.color,
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                        borderRadius: 4,
                        display: 'inline-block',
                        height: 24,
                        verticalAlign: 'middle',
                        width: 48,
                    }}
                />
            ),
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.bindingColumns.visibility`,
            ),
            key: 'visibility',
            width: 130,
            render: (_value, record) =>
                renderDisplayCategoryVisibility(record.category),
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.bindingColumns.actions`),
            key: 'actions',
            fixed: screens.sm ? 'right' : undefined,
            width: 300,
            render: (_value, record) => {
                if (!canUpdateMapping) {
                    return null
                }
                const visible = record.category.visible_to_user !== false
                return (
                    <Space size={4} wrap>
                        <Button
                            icon={<LinkOutlined />}
                            onClick={() => openBindingModal(record.key)}
                            type="link"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.bindClasses`,
                            )}
                        </Button>
                        <Button
                            icon={<EditOutlined />}
                            onClick={() =>
                                openDisplayCategoryModal(record.category)
                            }
                            type="link"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.editCategory`,
                            )}
                        </Button>
                        <Button
                            icon={
                                visible ? (
                                    <EyeInvisibleOutlined />
                                ) : (
                                    <EyeOutlined />
                                )
                            }
                            loading={visibilityActionKey === record.key}
                            onClick={() =>
                                void handleDisplayCategoryVisibilityUpdate(
                                    record.category,
                                    !visible,
                                )
                            }
                            type="link"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.${visible ? 'hideCategory' : 'showCategory'}`,
                            )}
                        </Button>
                        <NonDismissiblePopconfirm
                            title={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.confirmDeleteCategory`,
                            )}
                            onConfirm={() => {
                                void handleDeleteDisplayCategory(
                                    record.category,
                                )
                            }}
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                type="link"
                            >
                                {t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.deleteCategory`,
                                )}
                            </Button>
                        </NonDismissiblePopconfirm>
                    </Space>
                )
            },
        },
    ]

    const projectConfigurationColumns: TableColumnsType<ProjectConfigRecordRow> =
        [
            {
                title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.projectColumns.name`),
                dataIndex: 'projectName',
                key: 'projectName',
                render: renderPaginatedListText,
            },
            {
                title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.projectColumns.code`),
                dataIndex: 'projectCode',
                key: 'projectCode',
                width: 180,
                render: renderPaginatedListText,
            },
            {
                title: t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.projectColumns.actions`,
                ),
                key: 'actions',
                fixed: 'right',
                width: 220,
                render: (_value, record) => (
                    <Space size={4} wrap>
                        <Button
                            onClick={() =>
                                void openProjectModelConfigDetail(
                                    record.projectId,
                                )
                            }
                            type="link"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.modelConfig`,
                            )}
                        </Button>
                        <NonDismissiblePopconfirm
                            title={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.confirmDeleteProjectConfig`,
                            )}
                            onConfirm={() =>
                                void handleDeleteProjectConfig(
                                    record.projectId,
                                    'runtime',
                                )
                            }
                        >
                            <Button
                                danger
                                disabled={deletingProjectConfig}
                                icon={<DeleteOutlined />}
                                type="link"
                            >
                                {t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.deleteProjectConfig`,
                                )}
                            </Button>
                        </NonDismissiblePopconfirm>
                    </Space>
                ),
            },
        ]

    const projectBindingColumns: TableColumnsType<ProjectConfigRecordRow> = [
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.projectColumns.name`),
            dataIndex: 'projectName',
            key: 'projectName',
            render: renderPaginatedListText,
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.projectColumns.code`),
            dataIndex: 'projectCode',
            key: 'projectCode',
            width: 180,
            render: renderPaginatedListText,
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.projectColumns.actions`),
            key: 'actions',
            fixed: 'right',
            width: 220,
            render: (_value, record) => (
                <Space size={4} wrap>
                    <Button
                        onClick={() =>
                            void openProjectBindingDetail(record.projectId)
                        }
                        type="link"
                    >
                        {t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.typeConfig`,
                        )}
                    </Button>
                    <NonDismissiblePopconfirm
                        title={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.confirmDeleteProjectConfig`,
                        )}
                        onConfirm={() =>
                            void handleDeleteProjectConfig(
                                record.projectId,
                                'mapping',
                            )
                        }
                    >
                        <Button
                            danger
                            disabled={deletingProjectConfig}
                            icon={<DeleteOutlined />}
                            type="link"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.deleteProjectConfig`,
                            )}
                        </Button>
                    </NonDismissiblePopconfirm>
                </Space>
            ),
        },
    ]

    const detailVersions = useMemo(
        () =>
            detailModelKey
                ? versions.filter(
                      (version) => version.model_key === detailModelKey,
                  )
                : [],
        [detailModelKey, versions],
    )

    const renderVersionClasses = (record: ModelVersionSummary) => {
        const classEntries = Object.entries(record.class_names || {}).sort(
            ([left], [right]) => {
                const leftNumber = Number(left)
                const rightNumber = Number(right)
                if (
                    Number.isFinite(leftNumber) &&
                    Number.isFinite(rightNumber)
                ) {
                    return leftNumber - rightNumber
                }
                return left.localeCompare(right)
            },
        )
        if (!classEntries.length) {
            return '-'
        }
        const packageLabels = Array.isArray(
            record.validation_result?.package_labels,
        )
            ? (record.validation_result.package_labels as Array<{
                  id: number
                  zh?: string
              }>)
            : []
        const zhById = new Map(
            packageLabels.map((label) => [label.id, label.zh]).filter(([, zh]) => zh),
        )
        return (
            <Space size={[4, 4]} wrap>
                {classEntries.map(([clsId, rawClass]) => (
                    <Tag key={`${record.id}-${clsId}-${rawClass}`}>
                        {zhById.get(Number(clsId))
                            ? `${zhById.get(Number(clsId))} (${rawClass})`
                            : formatRawClassLabel(rawClass)}
                    </Tag>
                ))}
            </Space>
        )
    }

    const detailColumns: TableColumnsType<ModelVersionSummary> = [
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.detailColumns.version`),
            dataIndex: 'version_label',
            key: 'version',
            width: 160,
            ellipsis: true,
            render: renderPaginatedListText,
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.detailColumns.status`),
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (_status, record) => renderVersionStatus(record),
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.detailColumns.file`),
            key: 'file',
            width: 240,
            render: (_value, record) => (
                <Space
                    direction="vertical"
                    size={0}
                    style={{ maxWidth: '100%', minWidth: 0 }}
                >
                    {renderModelFileName(record)}
                    <span>{formatBytes(record.artifact?.size_bytes)}</span>
                </Space>
            ),
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.detailColumns.classes`),
            key: 'classes',
            width: 260,
            render: (_value, record) => renderVersionClasses(record),
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.detailColumns.uploadedAt`),
            key: 'uploadedAt',
            width: 160,
            render: (_value, record) =>
                localeService.formatDateTime(getVersionUploadedAt(record)),
        },
        {
            title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.detailColumns.actions`),
            key: 'actions',
            fixed: 'right',
            width: 150,
            render: (_value, record) =>
                canActivate &&
                record.id &&
                getVersionStatusKey(record) !== 'selected' ? (
                    <Space size={4} wrap>
                        <Button
                            disabled={actionLoadingId === record.id}
                            icon={<CheckCircleOutlined />}
                            onClick={() => void handleEnableVersion(record)}
                            type="link"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.enableVersion`,
                            )}
                        </Button>
                    </Space>
                ) : null,
        },
    ]

    const activeProjectRecords =
        mode === 'types' ? projectTypeConfigRecords : projectModelConfigRecords
    const activeProjectRecord = activeProjectId
        ? activeProjectRecords.find(
              (record) => record.projectId === activeProjectId,
          )
        : undefined
    const activeProjectLabel = activeProjectRecord
        ? t(`${MODEL_MANAGEMENT_I18N_PREFIX}.scope.projectOption`, {
              code: activeProjectRecord.projectCode,
              name: activeProjectRecord.projectName,
              defaultValue: `${activeProjectRecord.projectName} / ${activeProjectRecord.projectCode}`,
          })
        : ''

    const currentSectionTitle = t(
        `${MODEL_MANAGEMENT_I18N_PREFIX}.sections.${mode === 'types' ? 'recognitionTypes' : mode}`,
    )

    return (
        <PageLayout
            title={currentSectionTitle}
            breadcrumb={[
                {
                    title: t('navigation:breadcrumb.modelManagement', {
                        defaultValue: t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.title`,
                        ),
                    }),
                },
                { title: currentSectionTitle },
            ]}
        >
            {mode === 'upload' ? (
                <ModelUploadSection
                    canUpload={canUpload}
                    columns={versionColumns}
                    loading={loading}
                    onOpenUpload={() => openUploadModal()}
                    rows={versions}
                />
            ) : null}
            {mode === 'configuration' ? (
                <ModelConfigurationSection
                    activeScopeTab={scopeTab}
                    columns={configurationColumns}
                    creatingProjectConfig={creatingProjectConfig}
                    hasProjectConfigRecords={
                        projectModelConfigRecords.length > 0
                    }
                    loading={
                        scopeTab === 'project'
                            ? projectConfigRecordsLoading
                            : loading
                    }
                    onOpenCreateProjectConfig={openProjectConfigModal}
                    onProjectSearch={applyProjectSearch}
                    onScopeTabChange={handleScopeTabChange}
                    onProjectSearchReset={resetProjectSearch}
                    projectColumns={projectConfigurationColumns}
                    projectRows={filteredProjectModelConfigRecords}
                    projectSearch={projectSearch}
                    selectedProjectIds={selectedProjectIds}
                    onProjectSearchChange={setProjectSearch}
                    onSelectedProjectIdsChange={setSelectedProjectIds}
                    onBatchAction={handleBatchAction}
                    batchLoading={batchLoading}
                    rows={modelRows}
                />
            ) : null}
            {mode === 'types' ? (
                <>
                    <Tabs
                        activeKey={recognitionTypeView}
                        items={[
                            {
                                key: 'custom',
                                label: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.typeViews.custom`,
                                ),
                            },
                            {
                                key: 'model',
                                label: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.typeViews.model`,
                                ),
                            },
                            {
                                key: 'manual',
                                label: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.typeViews.manual`,
                                ),
                            },
                        ]}
                        onChange={handleRecognitionTypeViewChange}
                    />
                    {recognitionTypeView === 'custom' ? (
                        <ModelBindingSection
                            activeScopeTab={scopeTab}
                            canManageCategories={canUpdateMapping}
                            columns={bindingColumns}
                            creatingProjectConfig={creatingProjectConfig}
                            hasProjectConfigRecords={
                                projectTypeConfigRecords.length > 0
                            }
                            loading={
                                scopeTab === 'project'
                                    ? projectConfigRecordsLoading
                                    : mappingLoading
                            }
                            onOpenCreateCategory={() =>
                                openDisplayCategoryModal()
                            }
                            onOpenCreateProjectConfig={openProjectConfigModal}
                            onProjectSearch={applyTypeProjectSearch}
                            onProjectSearchChange={setTypeProjectSearch}
                            onProjectSearchReset={resetTypeProjectSearch}
                            onScopeTabChange={handleScopeTabChange}
                            projectColumns={projectBindingColumns}
                            projectRows={filteredProjectTypeConfigRecords}
                            projectSearch={typeProjectSearch}
                            rows={bindingRows}
                        />
                    ) : recognitionTypeView === 'model' ? (
                        <ModelRecognitionTypesSection
                            canUpdate={canUpdateMapping}
                        />
                    ) : (
                        <ManualRecognitionTypesSection
                            canUpdate={canUpdateMapping}
                        />
                    )}
                </>
            ) : null}

            <ModelUploadModal
                form={uploadForm}
                onClose={closeUploadModal}
                onSubmit={() => void handleUpload()}
                open={uploadModalOpen}
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                uploading={uploading}
            />

            <ModelRemarkModal
                form={remarkForm}
                onClose={closeRemarkModal}
                onSubmit={() => void handleRemarkSave()}
                open={remarkVersion !== null}
                saving={remarkSaving}
            />

            <Modal
                title={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.usage.title`, {
                    version: usageVersion?.version_label,
                })}
                open={usageVersion !== null}
                footer={null}
                onCancel={() => {
                    setUsageVersion(null)
                    setUsagePlatformInUse(false)
                    setSelectedUsageConfigKeys([])
                    setDetailLogContext(null)
                }}
                maskClosable={false}
                width={860}
            >
                <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: '100%' }}
                >
                    {usagePlatformInUse ? (
                        <span>
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.usage.platformSummary`,
                            )}
                        </span>
                    ) : null}
                    <span>
                        {t(`${MODEL_MANAGEMENT_I18N_PREFIX}.usage.summary`, {
                            count: usageRows.filter(
                                (row) => row.scopeType === 'project',
                            ).length,
                        })}
                    </span>
                    <Space wrap>
                        <Input
                            allowClear
                            aria-label={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.usage.searchPlaceholder`,
                            )}
                            onChange={(event) =>
                                setUsageSearch(event.target.value)
                            }
                            placeholder={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.usage.searchPlaceholder`,
                            )}
                            style={{ width: 260 }}
                            value={usageSearch}
                        />
                        {(['change', 'pause', 'resume'] as const).map(
                            (action) => (
                                <Button
                                    disabled={
                                        selectedUsageConfigKeys.length === 0
                                    }
                                    key={action}
                                    loading={batchLoading}
                                    onClick={() => handleBatchAction(action)}
                                >
                                    {t(
                                        `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.batch.${action}`,
                                    )}
                                </Button>
                            ),
                        )}
                    </Space>
                    <Table
                        columns={[
                            {
                                title: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.usage.configName`,
                                ),
                                dataIndex: 'projectName',
                                width: 190,
                            },
                            {
                                title: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.projectColumns.code`,
                                ),
                                dataIndex: 'projectCode',
                                width: 130,
                            },
                            {
                                title: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.usage.recognitionStatus`,
                                ),
                                key: 'recognitionStatus',
                                width: 110,
                                render: (
                                    _value,
                                    row: ModelUsageConfigRow,
                                ) => (
                                    <Tag
                                        color={
                                            row.participateInInference
                                                ? 'success'
                                                : 'warning'
                                        }
                                    >
                                        {t(
                                            `${MODEL_MANAGEMENT_I18N_PREFIX}.runtimeFlags.inference.${row.participateInInference ? 'on' : 'off'}`,
                                        )}
                                    </Tag>
                                ),
                            },
                            {
                                title: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.projectColumns.actions`,
                                ),
                                key: 'actions',
                                width: 310,
                                render: (
                                    _value,
                                    row: ModelUsageConfigRow,
                                ) => (
                                    <Space size={4} wrap={false}>
                                        <Button
                                            onClick={() => openUsageConfigDetail(row)}
                                            type="link"
                                        >
                                            {t(
                                                `${MODEL_MANAGEMENT_I18N_PREFIX}.usage.changeCurrent`,
                                            )}
                                        </Button>
                                        <Button
                                            onClick={() =>
                                                void handleUsageRuntimeAction(
                                                    row,
                                                    !row.participateInInference,
                                                )
                                            }
                                            type="link"
                                        >
                                            {t(
                                                `${MODEL_MANAGEMENT_I18N_PREFIX}.usage.${row.participateInInference ? 'pauseRecognition' : 'resumeRecognition'}`,
                                            )}
                                        </Button>
                                    </Space>
                                ),
                            },
                        ]}
                        dataSource={filteredUsageRows}
                        loading={usageLoading}
                        pagination={buildPaginatedListPagination({
                            defaultPageSize: 10,
                            showTotal: (total) =>
                                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                                    total,
                                }),
                        })}
                        rowKey="usageKey"
                        rowSelection={{
                            selectedRowKeys: selectedUsageConfigKeys,
                            onChange: setSelectedUsageConfigKeys,
                        }}
                        scroll={{ x: MODEL_USAGE_TABLE_SCROLL_X }}
                    />
                </Space>
            </Modal>
            <Modal
                title={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.batchChange.title`, {
                    count: usageVersion
                        ? selectedUsageConfigKeys.length
                        : selectedProjectIdValues.length,
                })}
                open={batchChangeOpen}
                confirmLoading={batchLoading}
                okText={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.confirm`)}
                onCancel={() => setBatchChangeOpen(false)}
                onOk={() => void handleBatchChange()}
                maskClosable={false}
                okButtonProps={{
                    disabled:
                        batchOperation === 'change' && !batchTargetVersionId,
                }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                        aria-label={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.batchChange.modelType`,
                        )}
                        options={MODEL_KEY_OPTIONS.map((option) => ({
                            label: t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.${option.labelKey}`,
                            ),
                            value: option.value,
                        }))}
                        onChange={(value) => {
                            setBatchModelKey(value)
                            setBatchTargetVersionId(undefined)
                        }}
                        disabled={usageVersion !== null}
                        value={batchModelKey}
                    />
                    {batchOperation === 'change' ? (
                        <Select
                            aria-label={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.batchChange.targetVersion`,
                            )}
                            options={versions
                                .filter(
                                    (version) =>
                                        version.model_key ===
                                            (usageVersion?.model_key ??
                                                batchModelKey) &&
                                        version.id,
                                )
                                .map((version) => ({
                                    disabled:
                                        usageVersion?.id === version.id,
                                    label: version.version_label,
                                    value: version.id as number,
                                }))}
                            onChange={setBatchTargetVersionId}
                            placeholder={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.batchChange.targetVersion`,
                            )}
                            value={batchTargetVersionId}
                        />
                    ) : (
                        <span>
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.batchConfirm.${batchOperation}Content`,
                            )}
                        </span>
                    )}
                </Space>
            </Modal>
            <Modal
                title={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.projectConfigModal.title`,
                )}
                open={projectConfigModalOpen}
                confirmLoading={creatingProjectConfig}
                onCancel={() => setProjectConfigModalOpen(false)}
                onOk={() => void handleCreateProjectConfig()}
                okText={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.submitProjectConfig`,
                )}
                maskClosable={false}
                destroyOnClose
            >
                <Form layout="vertical">
                    <Form.Item
                        label={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.projectConfigModal.project`,
                        )}
                        required
                    >
                        <Select
                            aria-label={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.projectConfigModal.project`,
                            )}
                            filterOption={filterSearchOptionByLabel}
                            optionFilterProp="label"
                            onChange={setProjectConfigModalValue}
                            options={availableProjectOptions}
                            placeholder={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.scope.projectPlaceholder`,
                            )}
                            showSearch
                            value={projectConfigModalValue}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.projectModelConfigModal.title`,
                    {
                        project: activeProjectLabel,
                    },
                )}
                open={projectConfigDetailOpen}
                onCancel={closeProjectModelConfigDetail}
                footer={null}
                maskClosable={false}
                width={MODEL_CONFIGURATION_DETAIL_MODAL_WIDTH}
                destroyOnClose
            >
                <Table
                    rowKey="modelKey"
                    loading={loading}
                    dataSource={modelRows}
                    columns={configurationColumns}
                    size={PAGINATED_LIST_TABLE_SIZE}
                    pagination={false}
                    scroll={{ x: MODEL_CONFIGURATION_TABLE_SCROLL_X }}
                />
            </Modal>

            <Modal
                title={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.projectTypeConfigModal.title`,
                    {
                        project: activeProjectLabel,
                    },
                )}
                open={projectBindingDetailOpen}
                onCancel={closeProjectBindingDetail}
                footer={null}
                maskClosable={false}
                width={1080}
                destroyOnClose
            >
                {canUpdateMapping ? (
                    <Space
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginBottom: 16,
                        }}
                    >
                        <Button
                            icon={<PlusOutlined />}
                            onClick={() => openDisplayCategoryModal()}
                            type="primary"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.createCategory`,
                            )}
                        </Button>
                    </Space>
                ) : null}
                <Table
                    rowKey="key"
                    loading={mappingLoading}
                    dataSource={bindingRows}
                    columns={bindingColumns}
                    size={PAGINATED_LIST_TABLE_SIZE}
                    pagination={buildPaginatedListPagination({
                        defaultPageSize: 10,
                        showTotal: (total) =>
                            t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                                total,
                            }),
                    })}
                    scroll={{ x: BINDING_TABLE_SCROLL_X }}
                />
            </Modal>

            <Modal
                title={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.displayCategoryModal.${
                        editingDisplayCategory ? 'editTitle' : 'createTitle'
                    }`,
                )}
                open={displayCategoryModalOpen}
                confirmLoading={savingDisplayCategory}
                onCancel={() => {
                    setDisplayCategoryModalOpen(false)
                    setEditingDisplayCategory(null)
                }}
                onOk={() => void handleDisplayCategorySubmit()}
                okText={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.submitCategory`,
                )}
                maskClosable={false}
                destroyOnClose
            >
                <Form form={displayCategoryForm} layout="vertical">
                    <Form.Item
                        label={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.displayNameZhCn`,
                        )}
                        name="displayNameZhCn"
                        rules={[
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.categoryNameRequired`,
                                ),
                                required: true,
                                whitespace: true,
                            },
                            {
                                max: 64,
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.categoryNameTooLong`,
                                ),
                            },
                        ]}
                    >
                        <Input
                            aria-label={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.form.displayNameZhCn`,
                            )}
                            maxLength={64}
                        />
                    </Form.Item>
                    {scope.scopeType === 'platform' ? (
                        <Form.Item
                            label={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.form.syncToProjects`,
                            )}
                            name="syncToProjects"
                            rules={[{ required: true }]}
                        >
                            <Select
                                placeholder={t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.form.syncToProjectsPlaceholder`,
                                )}
                                options={[
                                    {
                                        label: t(
                                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.syncToProjectsYes`,
                                        ),
                                        value: true,
                                    },
                                    {
                                        label: t(
                                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.syncToProjectsNo`,
                                        ),
                                        value: false,
                                    },
                                ]}
                            />
                        </Form.Item>
                    ) : null}
                    <Form.Item
                        label={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.displayCategoryColor`,
                        )}
                        name="color"
                        rules={[
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorRequired`,
                                ),
                                required: true,
                            },
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorInvalid`,
                                ),
                                pattern: /^#[0-9A-Fa-f]{6}$/,
                            },
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorReserved`,
                                ),
                                validator: (_rule, value) =>
                                    value?.toLowerCase() === RESERVED_TYPE_COLOR
                                        ? Promise.reject()
                                        : Promise.resolve(),
                            },
                        ]}
                    >
                        <TypeColorSelect
                            aria-label={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.form.displayCategoryColor`,
                            )}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <ModelBindingModal
                activeClassOptions={bindingModalRawClassOptions}
                activeManualTypeOptions={bindingModalManualTypeOptions}
                form={bindingForm}
                onClose={() => setBindingModalOpen(false)}
                onSubmit={() => void handleBindingSubmit()}
                open={bindingModalOpen}
                saving={savingBinding}
            />

            <ModelDetailModal
                columns={detailColumns}
                modelLabel={
                    detailModelKey ? getModelKeyLabel(detailModelKey) : ''
                }
                onClose={() => {
                    setDetailModalOpen(false)
                    if (!projectConfigDetailOpen) {
                        setDetailLogContext(null)
                    }
                }}
                open={detailModalOpen}
                versions={detailVersions}
            />
        </PageLayout>
    )
}

export default ModelManagement
